<template>
  <!--
    Footer nacional portado del componente React de Frn_PaginaWeb
    (src/components/Footer.tsx) y del Figma "Footer / Web Nacional"
    (node 3724:18784).

    Diferencia deliberada con el original: allá el contenido llega desde
    Strapi vía GraphQL; aquí los enlaces y textos van en duro, porque el
    portal no tiene Apollo ni acceso al CMS.
  -->
  <footer class="site-footer">
    <div class="site-footer__inner">
      <img :src="logo" alt="HIR Casa" class="site-footer__logo">

      <div class="site-footer__sections">
        <nav class="site-footer__column" aria-labelledby="footer-productos">
          <p id="footer-productos" class="site-footer__heading">Productos</p>
          <a
            v-for="link in productos"
            :key="link.label"
            :href="link.url"
            class="site-footer__link"
            target="_blank"
            rel="noreferrer noopener"
          >{{ link.label }}</a>
        </nav>

        <nav class="site-footer__column" aria-labelledby="footer-herramientas">
          <p id="footer-herramientas" class="site-footer__heading">Herramientas</p>
          <a
            v-for="link in herramientas"
            :key="link.label"
            :href="link.url"
            class="site-footer__link"
            target="_blank"
            rel="noreferrer noopener"
          >{{ link.label }}</a>
        </nav>

        <section class="site-footer__column site-footer__column--contacto">
          <p class="site-footer__heading">¿Tienes dudas? Estamos para ayudarte</p>

          <div class="site-footer__contact-row">
            <img :src="iconPhone" alt="" class="site-footer__icon" aria-hidden="true">
            <a href="tel:+525555119910" class="site-footer__link">55 5511 9910</a>
            <span class="site-footer__divider" aria-hidden="true" />
            <a href="tel:+528004472272" class="site-footer__link">800 hircasa (447 2272)</a>
          </div>

          <div class="site-footer__contact-row">
            <img :src="iconEmail" alt="" class="site-footer__icon" aria-hidden="true">
            <a href="mailto:atencionaclientes@hircasa.com.mx" class="site-footer__link">
              atencionaclientes@hircasa.com.mx
            </a>
          </div>

          <div class="site-footer__contact-row site-footer__contact-row--top">
            <img :src="iconLocation" alt="" class="site-footer__icon site-footer__icon--location" aria-hidden="true">
            <a
              :href="UBICACION_URL"
              class="site-footer__link"
              target="_blank"
              rel="noreferrer noopener"
            >
              Desierto de los Leones No. 46, Planta baja,
              Col. San Ángel, Álvaro Obregón, C.P. 01000, Ciudad de México.
            </a>
          </div>

          <p class="site-footer__heading">Horario de atención</p>

          <div class="site-footer__contact-row">
            <img :src="iconSchedule" alt="" class="site-footer__icon" aria-hidden="true">
            <span class="site-footer__text">Lun – Vie : 09:00 – 18:00</span>
          </div>
        </section>
      </div>

      <hr class="site-footer__rule">

      <div class="site-footer__bottom">
        <div class="site-footer__social">
          <a
            v-for="red in redes"
            :key="red.nombre"
            :href="red.url"
            class="site-footer__social-link"
            target="_blank"
            rel="noreferrer noopener"
            :aria-label="`Visita nuestras redes sociales en ${red.nombre}`"
            :title="`Visita nuestras redes sociales en ${red.nombre}`"
          >
            <img :src="red.icono" alt="" :class="['site-footer__social-icon', red.clase]">
          </a>
        </div>

        <nav class="site-footer__legal">
          <a
            v-for="link in legales"
            :key="link.label"
            :href="link.url"
            class="site-footer__legal-link"
            target="_blank"
            rel="noreferrer noopener"
          >{{ link.label }}</a>
        </nav>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
const asset = (nombre: string) => new URL(`../../../assets/footer/${nombre}`, import.meta.url).href

const logo = new URL('../../../../../assets/logoblanco.png', import.meta.url).href

const iconPhone = asset('ic_footer_phone.svg')
const iconEmail = asset('ic_footer_email.svg')
const iconLocation = asset('ic_footer_location.svg')
const iconSchedule = asset('ic_footer_schedule.svg')

/* URLs tomadas del footer real de https://hircasa.com (verificadas en sitio). */
const SITIO = 'https://hircasa.com'

const productos = [
  { label: '¿Cómo funciona?', url: `${SITIO}/como-funciona` },
  { label: 'Casa o departamento', url: `${SITIO}/financiamiento-para-casa-departamento` },
  { label: 'Local comercial', url: `${SITIO}/financiamiento-local-comercial` },
  { label: 'Construye o remodela', url: `${SITIO}/financiamiento-para-construir-o-remodelar-tu-inmueble` },
  { label: 'Preventa', url: `${SITIO}/financiamiento-para-adquirir-tu-inmueble-en-preventa` },
  { label: 'Terreno', url: `${SITIO}/financiamiento-para-comprar-terreno` },
  { label: 'Contrato de adhesión', url: `${SITIO}/contrato-adhesion` }
]

const herramientas = [
  { label: 'Blog', url: `${SITIO}/blog` },
  { label: 'Sucursales', url: `${SITIO}/sucursales-hir-casa` },
  { label: 'Calculadora', url: `${SITIO}/cotiza-tu-financiamiento-hir-casa` },
  { label: 'Portal clientes', url: 'https://miportal.hircasa.com/login' },
  { label: 'Preguntas frecuentes', url: `${SITIO}/preguntas-frecuentes` },
  { label: 'Inversionistas', url: 'https://investors.hircasa.com' },
  { label: 'Grupo Hir', url: 'https://grupohir.com.mx' },
  // Aún no publicado en hircasa.com: apunta a QA hasta que salga a producción.
  { label: 'Estrategia ESG', url: 'https://hircasa-frn-qa.hircasa.com.mx/sostenibilidad' }
]

const redes = [
  { nombre: 'Facebook', url: 'https://www.facebook.com/HIRCasaMexico/', icono: asset('ic_social_facebook.svg'), clase: '' },
  { nombre: 'X', url: 'https://x.com/hircasamexico', icono: asset('ic_social_x.svg'), clase: 'site-footer__social-icon--x' },
  { nombre: 'Instagram', url: 'https://www.instagram.com/hircasa/reels/', icono: asset('ic_social_instagram.svg'), clase: '' },
  { nombre: 'LinkedIn', url: 'https://mx.linkedin.com/company/hir-casa', icono: asset('ic_social_linkedin.svg'), clase: '' },
  { nombre: 'YouTube', url: 'https://www.youtube.com/user/HIRCASAMEXICO', icono: asset('ic_social_youtube.svg'), clase: 'site-footer__social-icon--youtube' }
]

const legales = [
  { label: 'Aviso de privacidad', url: `${SITIO}/aviso-privacidad` },
  { label: 'Términos y Condiciones', url: `${SITIO}/terminos-y-condiciones` },
  { label: 'Queremos escucharte', url: `${SITIO}/quejas-y-sugerencias` }
]

/* El sitio enlaza la dirección a esta ficha de Google Maps. */
const UBICACION_URL =
  'https://www.google.com/maps/place/HIR+Impulsores+Inmobiliarios/@19.3493052,-99.1888453,133m/data=!3m1!1e3!4m15!1m7!3m6!1s0x85d1ff0d1f932761:0xbaf86f03b4e137d7!2sCorporativo+HIR+Casa!8m2!3d19.3493389!4d-99.1887269!16s%2Fg%2F1pp2vhxmr!3m6!1s0x85d1ffa4f3dd244b:0x55bef06c530ddf38!8m2!3d19.3493179!4d-99.188793'
</script>

<style scoped>
.site-footer {
  background: var(--ds-purple-900);
  padding: 56px 24px;
}

.site-footer__inner {
  max-width: 1160px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 48px;
}

.site-footer__logo {
  width: 161px;
  height: auto;
  display: block;
}

.site-footer__sections {
  display: grid;
  grid-template-columns: 272px 272px 1fr;
  gap: 24px;
}

.site-footer__column {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}

.site-footer__heading {
  margin: 0;
  color: var(--ds-grey-50);
  font-size: 16px;
  font-weight: 600;
  line-height: normal;
}

.site-footer__link,
.site-footer__text {
  color: var(--ds-grey-50);
  font-size: 16px;
  font-weight: 400;
  line-height: normal;
  text-decoration: none;
}

.site-footer__link:hover {
  text-decoration: underline;
}

.site-footer__contact-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.site-footer__contact-row--top {
  align-items: flex-start;
}

/* Los SVG conservan su tamaño nativo: estirarlos los deforma. */
.site-footer__icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.site-footer__icon--location {
  width: 22px;
}

.site-footer__divider {
  width: 1px;
  height: 24px;
  background: var(--ds-grey-50);
  flex-shrink: 0;
}

.site-footer__rule {
  height: 2px;
  margin: 0;
  border: none;
  border-radius: 2px;
  background: var(--ds-grey-50);
}

.site-footer__bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.site-footer__social {
  display: flex;
  align-items: center;
  gap: 24px;
}

.site-footer__social-icon {
  width: 30px;
  height: 30px;
  display: block;
}

.site-footer__social-icon--x {
  height: 27px;
}

.site-footer__social-icon--youtube {
  height: 20px;
}

.site-footer__legal {
  display: flex;
  align-items: center;
  gap: 82px;
}

.site-footer__legal-link {
  color: var(--ds-grey-50);
  font-size: 16px;
  font-weight: 600;
  text-align: center;
  text-decoration: none;
}

.site-footer__legal-link:hover {
  text-decoration: underline;
}

@media (max-width: 1024px) {
  .site-footer__sections {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .site-footer__column--contacto {
    grid-column: 1 / -1;
  }

  .site-footer__bottom {
    flex-direction: column;
    align-items: flex-start;
  }

  .site-footer__legal {
    flex-wrap: wrap;
    gap: 24px;
  }
}

@media (max-width: 600px) {
  .site-footer {
    padding: 40px 24px;
  }

  .site-footer__sections {
    grid-template-columns: 1fr;
  }

  .site-footer__bottom {
    align-items: center;
  }

  .site-footer__social {
    justify-content: center;
  }

  .site-footer__legal {
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }
}
</style>
